import { useMutation } from "@apollo/client";
import { useState } from "react";
import { toast } from "react-toastify";
import { SYNC_FANCLUB_LIST } from "../../../queries/mutation/broadcaster/vipQueries";

type SyncResult = {
  applied: boolean;
  listCount: number;
  added: string[];
  removed: string[];
  unchanged: number;
  skipped: string[];
  currentCount: number;
};

type Props = {
  onClose: () => void;
  onApplied: () => void;
};

// A half-copied list would strip VIP from everyone missing from it, so a big
// removal needs an explicit "yes, this is the whole list".
const isBigRemoval = (r: SyncResult) =>
  r.removed.length >= 5 && r.removed.length > r.currentCount * 0.25;

const NameList = ({
  names,
  className,
}: {
  names: string[];
  className: string;
}) => (
  <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto mt-1">
    {names.map((n) => (
      <span key={n} className={`badge badge-sm ${className}`}>
        {n}
      </span>
    ))}
  </div>
);

const FanclubSyncModal = ({ onClose, onApplied }: Props) => {
  const [list, setList] = useState("");
  const [preview, setPreview] = useState<SyncResult | null>(null);
  const [confirmedComplete, setConfirmedComplete] = useState(false);
  const [sync, { loading }] = useMutation(SYNC_FANCLUB_LIST);

  const run = async (apply: boolean) => {
    try {
      const { data } = await sync({ variables: { list, apply } });
      const result: SyncResult = data.syncFanclubList;
      if (apply) {
        toast.success(
          `Fan club synced: ${result.added.length} added, ${result.removed.length} removed`,
        );
        onApplied();
      } else {
        setPreview(result);
        setConfirmedComplete(false);
      }
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const nothingChanges =
    preview && preview.added.length === 0 && preview.removed.length === 0;
  const needsConfirm = preview && isBigRemoval(preview);

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-2xl">
        <h3 className="font-bold text-lg">Sync fan club list</h3>

        {!preview ? (
          <>
            <p className="text-sm text-base-content/70 mt-2">
              Paste every current fan club member from Chaturbate, separated by
              commas. Everyone on the list becomes VIP, and anyone not on it
              stops being VIP. You'll see exactly what changes before anything
              happens.
            </p>
            <textarea
              className="textarea textarea-bordered w-full mt-3 h-40 font-mono text-sm"
              placeholder="username1, username2, username3"
              value={list}
              onChange={(e) => setList(e.target.value)}
              autoFocus
            />
            <div className="modal-action">
              <button className="btn" onClick={onClose} disabled={loading}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={() => run(false)}
                disabled={loading || !list.trim()}
              >
                {loading ? "Checking..." : "Preview"}
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm mt-2">
              {preview.listCount} usernames on the list ·{" "}
              {preview.currentCount} current members
            </p>

            <div className="mt-3 space-y-3">
              <div>
                <div className="text-sm font-semibold text-success">
                  Become VIP: {preview.added.length}
                </div>
                {preview.added.length > 0 && (
                  <NameList
                    names={preview.added}
                    className="badge-success badge-outline"
                  />
                )}
              </div>
              <div>
                <div className="text-sm font-semibold text-error">
                  Lose VIP: {preview.removed.length}
                </div>
                {preview.removed.length > 0 && (
                  <NameList
                    names={preview.removed}
                    className="badge-error badge-outline"
                  />
                )}
              </div>
              <div className="text-sm text-base-content/60">
                Already VIP, unchanged: {preview.unchanged}
              </div>
              {preview.skipped.length > 0 && (
                <div>
                  <div className="text-sm text-warning">
                    Not usernames, ignored: {preview.skipped.length}
                  </div>
                  <NameList names={preview.skipped} className="badge-ghost" />
                </div>
              )}
            </div>

            {needsConfirm && (
              <div className="alert alert-warning mt-4 text-sm">
                <div>
                  <div className="font-semibold">
                    This removes {preview.removed.length} of{" "}
                    {preview.currentCount} current members.
                  </div>
                  <div>
                    If you only copied part of the list, they'd lose VIP and
                    their Discord role.
                  </div>
                  <label className="label cursor-pointer justify-start gap-2 mt-1">
                    <input
                      type="checkbox"
                      // The default outline vanishes on the yellow alert.
                      className="checkbox checkbox-sm border-2 border-warning-content"
                      checked={confirmedComplete}
                      onChange={(e) => setConfirmedComplete(e.target.checked)}
                    />
                    <span>I've checked this is the complete list</span>
                  </label>
                </div>
              </div>
            )}

            <div className="modal-action">
              <button
                className="btn"
                onClick={() => setPreview(null)}
                disabled={loading}
              >
                Back
              </button>
              {nothingChanges ? (
                <button className="btn btn-primary" onClick={() => run(true)}>
                  Nothing to change - record sync
                </button>
              ) : (
                <button
                  className={`btn ${preview.removed.length ? "btn-warning" : "btn-primary"}`}
                  onClick={() => run(true)}
                  disabled={loading || (!!needsConfirm && !confirmedComplete)}
                >
                  {loading ? "Syncing..." : "Apply"}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default FanclubSyncModal;
